import { FieldErrors, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { VinInput } from "@common/components/form/vehicle";
import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
} from "react";
import { VinDecodeResponse } from "@modules/vehicle/types/VinDecodeResponse";
import { useUppercaseAlphaNumericMask } from "@talladega/hooks";

export interface VinFormInput {
  vin: string;
}

export interface VinFormMethods {
  validateForm: () => Promise<boolean>;
}

type Props = {
  vin: string;
  onSubmit: (vinDecodedData: VinDecodeResponse | undefined) => void;
  onVinDecode: (form: VinFormInput) => Promise<VinDecodeResponse | undefined>; // PROB VinDecodeResponse
  onVinChange?: (vin: string) => void;
  onError?: (errors: FieldErrors<VinFormInput>) => void;
};

const VinForm = forwardRef(
  (
    { vin, onSubmit, onVinDecode, onVinChange, onError }: Props,
    ref: ForwardedRef<VinFormMethods>
  ) => {
    const { t } = useTranslation(["common"]);
    const { maskPipe: maskUppercaseAlphaNumericPipe } =
      useUppercaseAlphaNumericMask();

    const findMyVehicleForm = useForm<VinFormInput>({
      defaultValues: {
        vin: vin ? maskUppercaseAlphaNumericPipe(vin) : "",
      },
      mode: "onChange",
    });

    const {
      handleSubmit,
      register,
      setError,
      trigger,
      formState: { errors, isSubmitting },
    } = findMyVehicleForm;

    const vinInputValue = useWatch({
      control: findMyVehicleForm.control,
      name: "vin",
    });

    const onFormSubmit: SubmitHandler<VinFormInput> = async (
      form: VinFormInput
    ) => {
      try {
        const vehicleInfo = await onVinDecode(form);

        onSubmit(vehicleInfo);
      } catch {
        setError("vin", {
          message: t("common:invalid_vin_please_try_again"),
        });
      }
    };

    useEffect(() => {
      if (onVinChange) onVinChange(vinInputValue);
    }, [vinInputValue, onVinChange]);

    useEffect(() => {
      if (onError) onError(errors);
    }, [errors, onError]);

    const validateForm = async () => {
      return trigger();
    };

    // Use `useImperativeHandle` to expose the validation method
    useImperativeHandle(ref, () => ({
      validateForm,
    }));

    return (
      <form
        onChange={handleSubmit(onFormSubmit)}
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <VinInput
          name="vin"
          register={register}
          errors={errors}
          required
          showWhereIsMyVin
          disabled={isSubmitting}
        />
      </form>
    );
  }
);

export default VinForm;
